"use client";

// 2026-08-23 GrowthChart — SVG growth curve (right) + verified capacity
// metrics (left). No user counts, no invented scale (Zero Clickbait).
// 2026-11-03 Minimalista PRO: sin recuadros pesados, sin textos dentro de la gráfica,
// estética editorial pura y trazo sobrio single-hue (Anti-Slop Pillar 3).

import { useEffect, useRef, useState } from "react";
import { useT } from "@/lib/use-i18n";
import { Reveal } from "./Reveal";
import { HERO_TOKENS } from "@/lib/design/hero-tokens";

export function GrowthChart() {
  const t = useT();
  const sectionRef = useRef<HTMLElement>(null);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const reduced =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    if (reduced || !sectionRef.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setAnimate(true);
          io.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    io.observe(sectionRef.current);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="growth"
      className="py-20 sm:py-28 overflow-hidden bg-background text-foreground transition-colors motion-reduce:transition-none"
      aria-labelledby="growth-heading"
    >
      <div className="mx-auto max-w-container-wide gutter-fluid">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
          {/* Left Column: Métricas tipográficas limpias sin recuadros */}
          <Reveal className="lg:col-span-5 space-y-8">
            <div>
              <h2
                id="growth-heading"
                className="text-[var(--step-3)] font-bold tracking-tight text-foreground leading-[1.12] text-balance"
              >
                {t("growthTitle")}
              </h2>
              <p className="mt-3.5 text-sm sm:text-base leading-relaxed text-muted-foreground text-pretty">
                {t("growthSubtitle")}
              </p>
            </div>

            <div className="space-y-6 pt-2">
              <div className="space-y-1">
                <div
                  className="text-xs font-mono uppercase tracking-wider tabular-nums text-muted-foreground"
                  title={t("growthStat1Label")}
                >
                  {t("growthStat1Label")}
                </div>
                <div className="text-[var(--step-4)] font-bold tracking-tight text-foreground mt-1 tabular-nums text-3xl sm:text-4xl">
                  {t("growthStat1Value")}
                </div>
              </div>

              <div className="h-px bg-border/40" aria-hidden="true" />

              <div className="space-y-1">
                <div
                  className="text-xs font-mono uppercase tracking-wider tabular-nums text-muted-foreground"
                  title={t("growthStat2Label")}
                >
                  {t("growthStat2Label")}
                </div>
                <div className="text-[var(--step-4)] font-bold tracking-tight text-foreground mt-1 tabular-nums text-3xl sm:text-4xl">
                  {t("growthStat2Value")}
                </div>
              </div>

              <div className="h-px bg-border/40" aria-hidden="true" />

              <div className="space-y-1">
                <div
                  className="text-xs font-mono uppercase tracking-wider tabular-nums text-muted-foreground"
                  title={t("growthStat3Label")}
                >
                  {t("growthStat3Label")}
                </div>
                <div className="text-[var(--step-4)] font-bold tracking-tight text-foreground mt-1 tabular-nums text-3xl sm:text-4xl">
                  {t("growthStat3Value")}
                </div>
              </div>
            </div>
          </Reveal>

          {/* Right Column: Curva minimalista pura — sin recuadro exterior, sin textos flotantes */}
          <Reveal className="lg:col-span-7 relative" delay={120}>
            <div
              className={`growth-chart relative h-[320px] sm:h-[420px] lg:h-[480px] w-full flex items-center justify-center${
                animate ? " growth-chart--anim" : ""
              }`}
            >
              <svg
                className="size-full"
                viewBox="0 0 720 400"
                preserveAspectRatio="xMidYMid meet"
                aria-hidden="true"
              >
                <defs>
                  {/* Area fill: whisper-sutil single-hue */}
                  <linearGradient
                    id="growthAreaGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor={HERO_TOKENS.blue}
                      stopOpacity="0.08"
                    />
                    <stop
                      offset="100%"
                      stopColor={HERO_TOKENS.blue}
                      stopOpacity="0"
                    />
                  </linearGradient>

                  {/* Stroke gradient: tono índigo elegante, sin arcoíris chillón */}
                  <linearGradient
                    id="growthLineGradient"
                    x1="0%"
                    y1="100%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop
                      offset="0%"
                      stopColor={HERO_TOKENS.blue}
                      stopOpacity="0.5"
                    />
                    <stop offset="100%" stopColor={HERO_TOKENS.blue} />
                  </linearGradient>
                </defs>

                {/* Baseline sutil — sin cuadrículas ni líneas superpuestas */}
                <line
                  x1="40"
                  y1="340"
                  x2="680"
                  y2="340"
                  stroke="currentColor"
                  className="text-border/20"
                  strokeWidth="1"
                />

                {/* Area fill */}
                <path
                  className="growth-area"
                  d="M 40,340 C 120,336 180,318 260,290 C 340,262 400,224 470,178 C 520,148 580,96 650,52 L 680,36 L 680,400 L 40,400 Z"
                  fill="url(#growthAreaGradient)"
                />

                {/* Línea orgánica limpia de 2px */}
                <path
                  className="growth-line"
                  pathLength={1}
                  d="M 40,340 C 120,336 180,318 260,290 C 340,262 400,224 470,178 C 520,148 580,96 650,52 L 680,36"
                  fill="none"
                  stroke="url(#growthLineGradient)"
                  strokeWidth="2"
                  vectorEffect="non-scaling-stroke"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                {/* Dots en el mismo tono sobrio */}
                <circle
                  className="growth-dot"
                  cx="40"
                  cy="340"
                  r="3.5"
                  fill={HERO_TOKENS.blue}
                />
                <circle
                  className="growth-dot growth-dot--end"
                  cx="680"
                  cy="36"
                  r="4"
                  fill={HERO_TOKENS.blue}
                />
              </svg>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
